import { CSSObject } from '@emotion/react'
import React from 'react'

import {
  AlignItems,
  Body2,
  Box,
  Display,
  Heading2,
  JustifyContent,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { CtaLink, mapCmsCta } from '../../cta'
import type { CmsCtaIntentHandlers } from '../../cta'
import { ContentstackImage } from '../ContentstackImage'
import type { LeftAlignedImageWithTextFields } from './types'

export type LeftAlignedImageWithTextProps = LeftAlignedImageWithTextFields & {
  ctaIntentHandlers?: CmsCtaIntentHandlers
}

const Container = withCitrusTheme(Box, (theme) => ({
  ...(theme.responsiveStyle(['column-reverse', null, null, 'row'], ['flexDirection']) as CSSObject),
  margin: '0 auto',
}))

function getBodyParagraphs(body?: string) {
  if (!body) {
    return []
  }

  return body
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
}

export function LeftAlignedImageWithText({
  heading,
  body,
  image,
  image_mobile,
  image_alt,
  cta,
  ctaIntentHandlers,
}: LeftAlignedImageWithTextProps) {
  const bodyParagraphs = getBodyParagraphs(body)
  const ctaLink = mapCmsCta(cta, ctaIntentHandlers)
  const imageSrc = image?.url ?? image_mobile?.url

  return (
    <Container
      display={Display.Flex}
      alignItems={AlignItems.FlexStart}
      justifyContent={JustifyContent.SpaceEvenly}
      paddingH={8}
      paddingV={16}
      gap={[8, null, null, 16]}
      maxWidth="1200px"
    >
      {imageSrc ? (
        <Box
          width={['100%', null, null, '50%']}
          style={{ borderRadius: '8px', overflow: 'hidden' }}
        >
          <ContentstackImage
            alt={image_alt || ''}
            fit="cover"
            sizes="(max-width: 1023px) 100vw, 600px"
            src={imageSrc}
            mobileSrc={image_mobile?.url}
            mobileMedia="(max-width: 1023px)"
            width="100%"
            style={{ display: 'block' }}
          />
        </Box>
      ) : null}

      <Box width={[null, null, null, imageSrc ? '50%' : '100%']} paddingRight={4}>
        <Heading2>{heading}</Heading2>

        {bodyParagraphs.length > 0 ? (
          <>
            <Space size={4} />
            {bodyParagraphs.map((paragraph, index) => (
              <React.Fragment key={`${paragraph}-${index}`}>
                {index > 0 ? <Space size={4} /> : null}
                <Body2 size={4}>{paragraph}</Body2>
              </React.Fragment>
            ))}
          </>
        ) : null}

        {ctaLink ? (
          <>
            <Space size={6} />
            <Box
              display={Display.Flex}
              justifyContent={[JustifyContent.Center, JustifyContent.FlexStart]}
            >
              <CtaLink cta={ctaLink} />
            </Box>
          </>
        ) : null}
      </Box>
    </Container>
  )
}
